import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService, documentService } from '../../services/loanService';
import { KYCStatusBadge } from '../../components/common/StatusBadge';
import { formatDate, formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Standard KYC document types required from applicants
const KYC_DOC_TYPES = [
  { value: 'id_front',         label: 'Government ID (Front)' },
  { value: 'id_back',          label: 'Government ID (Back)' },
  { value: 'selfie',           label: 'Selfie / Live Photo' },
  { value: 'proof_of_address', label: 'Proof of Address' },
  { value: 'bank_statement',   label: 'Bank Statement' },
  { value: 'pay_slip',         label: 'Pay Slip' },
];

const DOC_STATUS_META = {
  pending:           { label: 'Pending Review',    color: '#ff9900' },
  approved:          { label: 'Approved',           color: '#00C896' },
  rejected:          { label: 'Rejected',           color: '#FF4757' },
  resubmit_required: { label: 'Resubmit Required', color: '#818CF8' },
};

const REQUEST_STATUS_META = {
  pending:   { label: 'Awaiting Upload', color: '#ff9900' },
  fulfilled: { label: 'Uploaded',        color: '#5B8EFF' },
  waived:    { label: 'Waived',          color: '#94a3b8' },
};

const STAGE_FILTERS = [
  { value: '',             label: 'All',          desc: 'All registrations' },
  { value: 'pending',      label: 'Pending',      desc: 'Awaiting first review' },
  { value: 'under_review', label: 'Under Review', desc: 'Review in progress' },
  { value: 'resubmit',     label: 'Resubmit',     desc: 'Documents re-requested' },
  { value: 'approved',     label: 'Approved',     desc: 'KYC verified' },
  { value: 'rejected',     label: 'Rejected',     desc: 'Failed verification' },
];

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function Field({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.85rem', fontWeight: 500, color: color || 'var(--text-1)' }}>{value || '—'}</div>
    </div>
  );
}

function DocLink({ label, url }) {
  if (!url) return <span style={{ color: 'var(--text-3)', fontSize: '0.8rem' }}>—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer"
      style={{ color: 'var(--primary)', fontSize: '0.8rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
      {label} ↗
    </a>
  );
}

function StatusChip({ label, color }) {
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.55rem',
      borderRadius: 99, background: `${color}18`, color, border: `1px solid ${color}30`,
    }}>{label}</span>
  );
}

// ── Document request row (KYC) ────────────────────────────────────────────────

function KYCDocRequestRow({ req, onReviewDoc, onWaive, reviewing }) {
  const [showResubmit, setShowResubmit] = useState(false);
  const [resubmitReason, setResubmitReason] = useState('');

  const rMeta = REQUEST_STATUS_META[req.status] || { label: req.status, color: '#94a3b8' };
  const hasDoc = !!req.fulfilled_document_id;
  const dMeta = hasDoc ? (DOC_STATUS_META[req.fulfilled_document_status] || { label: req.fulfilled_document_status, color: '#94a3b8' }) : null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '0.625rem 1rem', background: 'var(--surface-alt)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontWeight: 600, fontSize: '0.83rem' }}>{req.doc_type_display || humanize(req.doc_type)}</span>
          <StatusChip label={rMeta.label} color={rMeta.color} />
        </div>
        {req.status === 'pending' && (
          <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '0.2rem 0.55rem' }}
            disabled={reviewing} onClick={() => onWaive(req.id)}>
            Waive
          </button>
        )}
      </div>

      {req.notes && (
        <div style={{ padding: '0.4rem 1rem', fontSize: '0.78rem', color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}>
          <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>Instructions: </span>{req.notes}
        </div>
      )}

      {hasDoc && (
        <div style={{ padding: '0.75rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <StatusChip label={dMeta.label} color={dMeta.color} />
              {req.fulfilled_document_file && (
                <a href={req.fulfilled_document_file} target="_blank" rel="noreferrer"
                  style={{ fontSize: '0.8rem', color: 'var(--primary)', textDecoration: 'none' }}>
                  View ↗
                </a>
              )}
            </div>
            {req.fulfilled_document_status === 'pending' && (
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="btn btn-primary"
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.55rem', background: '#1596E0', border: 'none' }}
                  disabled={reviewing}
                  onClick={() => onReviewDoc(req.fulfilled_document_id, 'approved')}>
                  Approve
                </button>
                <button className="btn btn-ghost"
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.55rem', color: '#818CF8', borderColor: '#818CF840' }}
                  onClick={() => setShowResubmit(v => !v)}>
                  Re-request
                </button>
                <button className="btn btn-ghost"
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.55rem', color: '#FF4757', borderColor: '#FF475740' }}
                  disabled={reviewing}
                  onClick={() => { const r = window.prompt('Rejection reason:'); if (r) onReviewDoc(req.fulfilled_document_id, 'rejected', r); }}>
                  Reject
                </button>
              </div>
            )}
          </div>
          {req.fulfilled_document_rejection_reason && (
            <p style={{ fontSize: '0.75rem', color: '#FF4757', marginTop: '0.35rem' }}>
              Reason: {req.fulfilled_document_rejection_reason}
            </p>
          )}
          {showResubmit && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem' }}>
              <input className="form-input" placeholder="Reason for re-requesting…"
                value={resubmitReason} onChange={e => setResubmitReason(e.target.value)}
                style={{ fontSize: '0.78rem', flex: 1 }} />
              <button className="btn btn-primary"
                style={{ fontSize: '0.7rem', padding: '0.25rem 0.7rem', background: '#818CF8', border: 'none', whiteSpace: 'nowrap' }}
                disabled={reviewing || !resubmitReason.trim()}
                onClick={() => {
                  onReviewDoc(req.fulfilled_document_id, 'resubmit_required', resubmitReason);
                  setShowResubmit(false); setResubmitReason('');
                }}>
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function KYCOfficerDashboard() {
  const qc = useQueryClient();

  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);   // summary row
  const [decisionErr, setDecisionErr] = useState('');
  const [decisionReason, setDecisionReason] = useState('');

  // Doc request panel state
  const [docSelections, setDocSelections] = useState({});  // { doc_type: notes }
  const [docReqErr, setDocReqErr] = useState('');
  const [docReqSuccess, setDocReqSuccess] = useState('');

  const toggleDocType = (v) => setDocSelections(prev => {
    const n = { ...prev };
    if (n[v] !== undefined) delete n[v]; else n[v] = '';
    return n;
  });
  const setDocNote = (v, note) => setDocSelections(prev => ({ ...prev, [v]: note }));

  // ── Counts for each stage (for the filter tabs) ──────────────────────────────
  const { data: allData } = useQuery({
    queryKey: ['kyc-officer-counts'],
    queryFn: () => adminService.getAllKYC({}).then(r => r.data.data),
    refetchInterval: 60000,
  });
  const stageCounts = {};
  for (const s of (allData?.results || allData || [])) {
    stageCounts[s.status] = (stageCounts[s.status] || 0) + 1;
  }

  // ── Filtered list ────────────────────────────────────────────────────────────
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['kyc-officer-list', filter],
    queryFn: () => adminService.getAllKYC(filter ? { status: filter } : {}).then(r => r.data.data),
  });
  const kycs = listData?.results || listData || [];
  const total = listData?.count ?? kycs.length;

  // ── Full KYC detail ──────────────────────────────────────────────────────────
  const { data: detail, isLoading: detailLoading, refetch: refetchDetail } = useQuery({
    queryKey: ['kyc-officer-detail', selected?.id],
    queryFn: () => adminService.getKYCDetail(selected.id).then(r => r.data.data),
    enabled: !!selected?.id,
  });

  // ── KYC document requests ────────────────────────────────────────────────────
  const { data: docReqs, refetch: refetchDocReqs } = useQuery({
    queryKey: ['kyc-officer-doc-reqs', selected?.id],
    queryFn: () => adminService.getKYCDocumentRequests(selected.id).then(r => r.data.data),
    enabled: !!selected?.id,
  });
  const docRequests = Array.isArray(docReqs) ? docReqs : [];

  // ── Mutations ────────────────────────────────────────────────────────────────
  const reviewMutation = useMutation({
    mutationFn: ({ id, status, rejection_reason }) => adminService.reviewKYC(id, { status, rejection_reason }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['kyc-officer-list'] });
      qc.invalidateQueries({ queryKey: ['kyc-officer-counts'] });
      qc.invalidateQueries({ queryKey: ['kyc-officer-detail', vars.id] });
      setDecisionErr('');
      setDecisionReason('');
      setSelected(prev => prev ? { ...prev, status: vars.status } : null);
    },
    onError: (e) => setDecisionErr(e.response?.data?.message || 'Action failed.'),
  });

  const docReqMutation = useMutation({
    mutationFn: ({ id, documents }) => adminService.kycRequestDocuments(id, { documents }),
    onSuccess: () => {
      refetchDocReqs();
      setDocSelections({});
      setDocReqErr('');
      setDocReqSuccess('Document requests sent to applicant.');
      setTimeout(() => setDocReqSuccess(''), 4000);
    },
    onError: (e) => setDocReqErr(e.response?.data?.message || 'Failed to send requests.'),
  });

  const reviewDocMutation = useMutation({
    mutationFn: ({ docId, status, rejection_reason }) =>
      documentService.reviewDocument(docId, { status, rejection_reason }),
    onSuccess: () => { refetchDocReqs(); refetchDetail(); },
  });

  const waiveDocMutation = useMutation({
    mutationFn: (reqId) => adminService.waiveKYCRequest(reqId),
    onSuccess: () => refetchDocReqs(),
  });

  const handleDecide = (status) => {
    if ((status === 'rejected' || status === 'resubmit') && !decisionReason.trim()) {
      setDecisionErr('A reason is required when rejecting or requesting resubmission.');
      return;
    }
    reviewMutation.mutate({ id: selected.id, status, rejection_reason: decisionReason });
  };

  const handleRequestDocs = () => {
    const selected_docs = Object.keys(docSelections);
    if (selected_docs.length === 0) { setDocReqErr('Select at least one document type.'); return; }
    docReqMutation.mutate({
      id: selected.id,
      documents: selected_docs.map(dt => ({ doc_type: dt, notes: docSelections[dt] })),
    });
  };

  const panelStatus = detail?.status ?? selected?.status;
  const actionable = ['pending', 'under_review', 'resubmit'].includes(panelStatus);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <h1>KYC Officer Dashboard</h1>
        <p>Review customer identity documents and manage KYC verification</p>
      </div>

      {/* ── Stage filter tabs ── */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {STAGE_FILTERS.map(({ value, label, desc }) => {
          const count = value ? (stageCounts[value] || 0) : Object.values(stageCounts).reduce((a, b) => a + b, 0);
          const active = filter === value;
          return (
            <button key={value}
              className={`btn ${active ? 'btn-primary' : 'btn-ghost'}`}
              style={{ fontSize: '0.78rem', padding: '0.35rem 0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              title={desc}
              onClick={() => { setFilter(value); setSelected(null); setDecisionErr(''); }}>
              {label}
              {count > 0 && (
                <span style={{
                  fontSize: '0.62rem', fontWeight: 700, minWidth: 18, height: 18,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 99, padding: '0 4px',
                  background: active ? 'rgba(255,255,255,0.25)' : value === 'pending' || value === 'resubmit' ? 'rgba(255,153,0,0.15)' : 'var(--surface-alt)',
                  color: active ? '#fff' : value === 'pending' || value === 'resubmit' ? '#ff9900' : 'var(--text-2)',
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-3)' }}>
          {total} record{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Two-column: list + detail ── */}
      {listLoading ? <LoadingSpinner /> : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '340px 1fr' : '1fr', gap: '1.125rem', alignItems: 'start' }}>

          {/* List */}
          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Stage</th>
                    <th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {kycs.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2.5rem' }}>
                        No records for this stage.
                      </td>
                    </tr>
                  ) : kycs.map(kyc => (
                    <tr key={kyc.id}
                      style={{ cursor: 'pointer', background: selected?.id === kyc.id ? 'var(--primary-light)' : 'inherit' }}
                      onClick={() => { setSelected(kyc); setDecisionErr(''); setDecisionReason(''); setDocReqErr(''); setDocReqSuccess(''); }}>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{kyc.user_name}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>{kyc.user_email}</div>
                      </td>
                      <td><KYCStatusBadge status={kyc.status} /></td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{formatDate(kyc.submitted_at)}</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 600, fontSize: '0.85rem' }}>→</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

              {/* ── Identity & Info ── */}
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{ fontWeight: 700, marginBottom: '0.15rem', fontSize: '1.05rem' }}>{selected.user_name}</h3>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', fontFamily: 'monospace' }}>{selected.user_email}</div>
                  </div>
                  <KYCStatusBadge status={panelStatus} />
                </div>

                {detailLoading ? (
                  <div style={{ padding: '1.5rem 0', textAlign: 'center' }}><LoadingSpinner /></div>
                ) : detail ? (
                  <>
                    {/* Identity fields */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.875rem', marginBottom: '1rem' }}>
                      <Field label="BVN"          value={detail.bvn} />
                      <Field label="NIN"          value={detail.nin} />
                      <Field label="ID Type"      value={humanize(detail.id_type || '')} />
                      <Field label="ID Number"    value={detail.id_number} />
                      <Field label="Employment"   value={humanize(detail.employment_status || '')} />
                      <Field label="Monthly Income" value={detail.monthly_income ? formatCurrency(detail.monthly_income) : null} />
                      <Field label="Employer"     value={detail.employer_name} />
                      <Field label="Bank"         value={detail.bank_name} />
                      <Field label="Account No."  value={detail.account_number} />
                    </div>

                    {/* Submitted documents */}
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.875rem', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.625rem' }}>
                        Submitted Documents
                      </div>
                      <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                        <DocLink label="Selfie"           url={detail.selfie} />
                        <DocLink label="Proof of Address" url={detail.proof_of_address} />
                        {detail.id_front && <DocLink label="ID Front" url={detail.id_front} />}
                        {detail.id_back  && <DocLink label="ID Back"  url={detail.id_back} />}
                      </div>
                    </div>

                    {/* Rejection reason if present */}
                    {detail.rejection_reason && (
                      <div style={{ padding: '0.5rem 0.75rem', background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 8, fontSize: '0.8rem', marginBottom: '0.875rem' }}>
                        <span style={{ fontWeight: 600, color: '#FF4757' }}>Rejection reason: </span>
                        <span style={{ color: 'var(--text-2)' }}>{detail.rejection_reason}</span>
                      </div>
                    )}

                    <div style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>
                      Submitted: {formatDate(detail.submitted_at)}
                      {detail.reviewed_at && ` · Reviewed: ${formatDate(detail.reviewed_at)}`}
                    </div>
                  </>
                ) : (
                  <div className="alert alert-error"><span>⚠</span><span>Could not load details.</span></div>
                )}
              </div>

              {/* ── KYC Document Requests panel ── */}
              {detail && (
                <div className="card">
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '1rem' }}>
                    Document Requests
                    {docRequests.length > 0 && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: 'var(--text-3)', fontWeight: 400 }}>
                        ({docRequests.filter(r => r.status === 'pending').length} pending)
                      </span>
                    )}
                  </div>

                  {/* Existing requests */}
                  {docRequests.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
                      {docRequests.map(req => (
                        <KYCDocRequestRow
                          key={req.id}
                          req={req}
                          reviewing={reviewDocMutation.isPending}
                          onReviewDoc={(docId, s, reason) => reviewDocMutation.mutate({ docId, status: s, rejection_reason: reason || '' })}
                          onWaive={(id) => waiveDocMutation.mutate(id)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Request new documents */}
                  {actionable && (
                    <div style={{ borderTop: docRequests.length > 0 ? '1px solid var(--border)' : 'none', paddingTop: docRequests.length > 0 ? '1rem' : 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-2)', marginBottom: '0.625rem' }}>
                        Request Documents from Applicant
                      </div>
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginBottom: '0.75rem' }}>
                        Select the standard KYC documents required. Optionally add instructions per document.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '0.875rem' }}>
                        {KYC_DOC_TYPES.map(({ value, label }) => {
                          const sel = docSelections[value] !== undefined;
                          return (
                            <div key={value} style={{
                              border: `1px solid ${sel ? 'rgba(91,142,255,0.4)' : 'var(--border)'}`,
                              borderRadius: 8, overflow: 'hidden',
                              background: sel ? 'rgba(91,142,255,0.05)' : 'var(--surface-alt)',
                              transition: 'all .15s',
                            }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.475rem 0.75rem', cursor: 'pointer' }}>
                                <input type="checkbox" checked={sel} onChange={() => toggleDocType(value)}
                                  style={{ accentColor: '#5B8EFF', width: 13, height: 13, flexShrink: 0 }} />
                                <span style={{ fontSize: '0.8rem', fontWeight: sel ? 600 : 400, color: sel ? '#5B8EFF' : 'var(--text-2)' }}>
                                  {label}
                                </span>
                              </label>
                              {sel && (
                                <div style={{ padding: '0 0.75rem 0.45rem' }}>
                                  <input className="form-input"
                                    placeholder={`Instructions for ${label} (optional)`}
                                    value={docSelections[value]}
                                    onChange={e => setDocNote(value, e.target.value)}
                                    style={{ fontSize: '0.76rem' }} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {docReqErr && (
                        <div className="alert alert-error" style={{ marginBottom: '0.625rem' }}>
                          <span>⚠</span><span>{docReqErr}</span>
                        </div>
                      )}
                      {docReqSuccess && (
                        <div className="alert alert-success" style={{ marginBottom: '0.625rem' }}>
                          <span>✓</span><span>{docReqSuccess}</span>
                        </div>
                      )}
                      <button className="btn btn-primary" style={{ width: '100%', fontSize: '0.82rem' }}
                        disabled={docReqMutation.isPending || Object.keys(docSelections).length === 0}
                        onClick={handleRequestDocs}>
                        {docReqMutation.isPending ? 'Sending…' : 'Send Document Requests'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── KYC Decision ── */}
              {detail && (
                <div className="card">
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '1rem' }}>KYC Decision</div>

                  {actionable ? (
                    <>
                      {decisionErr && (
                        <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
                          <span>⚠</span><span>{decisionErr}</span>
                        </div>
                      )}
                      <div className="form-group">
                        <label className="form-label">Reason (required for Resubmit / Reject)</label>
                        <textarea className="form-input" rows={2}
                          value={decisionReason}
                          onChange={e => { setDecisionReason(e.target.value); setDecisionErr(''); }}
                          placeholder="Explain why you are rejecting or requesting resubmission…" />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                        <button className="btn btn-primary"
                          style={{ background: '#1596E0', border: 'none', fontSize: '0.82rem' }}
                          disabled={reviewMutation.isPending}
                          onClick={() => handleDecide('approved')}>
                          {reviewMutation.isPending ? '…' : '✓ Approve'}
                        </button>
                        <button className="btn btn-ghost"
                          style={{ color: '#F5A623', borderColor: '#F5A62340', fontSize: '0.82rem' }}
                          disabled={reviewMutation.isPending}
                          onClick={() => handleDecide('resubmit')}>
                          ↺ Resubmit
                        </button>
                        <button className="btn btn-ghost"
                          style={{ color: '#FF4757', borderColor: '#FF475740', fontSize: '0.82rem' }}
                          disabled={reviewMutation.isPending}
                          onClick={() => handleDecide('rejected')}>
                          ✕ Reject
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: '0.75rem', background: 'var(--surface-alt)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--text-2)', textAlign: 'center' }}>
                      This KYC has been <strong>{panelStatus}</strong>.
                      {detail?.rejection_reason && (
                        <div style={{ marginTop: '0.25rem', color: '#FF4757', fontSize: '0.78rem' }}>
                          Reason: {detail.rejection_reason}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
}
